<template>
  <div class="investments container">
    <div class="row">
      <div class="col-lg-12">
        <Loader v-show="isLoading"/>
        <div v-if="!isLoading" class="container">
          <div v-if="!notFound">
            <button type="button" @click="$router.go(-1)" class="btn">&laquo;{{ $t('views.loan-details.laquoBack') }}</button>
            <h2>{{ loanDetails.loan_type_classificators_name }}: {{ loanDetails.loan_ref }}</h2>
            <div class="row">
              <div class="col col-lg-8 col-12 col-sm-12">
                <div class="income-box">
                  <div class="right-block">
                    <div>
                      <doughnut-chart :categories="chartData"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col col-lg-4 col-12 col-sm-12">
                <div class="income-box">
                  <div class="right-block">
                    <div class="header">{{ $t('views.loan-details.invest') }}</div>
                    <div class="invest-available">
                      <div class="afi">{{ $t('views.loan-details.availableForInvesting') }}</div>
                      <div class="amount">{{ this.loanDetails.investable_amount | moneyFormatter }}</div>
                    </div>
                    <div v-if="loanDetails.buyback_guarantee_days" class="note">{{ $t('views.loan-details.thisLoanComes', {'1': loanDetails.buyback_guarantee_days}) }}</div>
                    <div class="invest-section">
                      <b-button size="invest-btn" class="invest-btn" variant="light" v-show="!showInvestInput"
                                @click="showInvestForm()">{{ $t('views.loan-details.invest1') }}
                      </b-button>
                      <div class="invest-form" v-show="showInvestInput">
                        <b-input-group size="sm" class="invest-ig">
                          <b-input-group-prepend class="prepend">
                            €
                          </b-input-group-prepend>
                          <b-form-input
                              v-model="investInput"
                              v-bind:ref="loanDetails.loan_ref"
                              v-bind:class="(isValid() ? '' : 'invalid')"
                              v-bind:min="getMinAmount(loanDetails.investable_amount)"
                              v-bind:max="parseFloat(loanDetails.investable_amount).toFixed(2)"
                              @change="maxRound($event, loanDetails.investable_amount, loanDetails.loan_ref)"
                              type="number" step="any"
                              class="invest-input"></b-form-input>
                          <b-input-group-append class="append" @click="addInvest()">
                            <img v-bind:src="getSrc('portfolio.svg')" alt=""/>
                          </b-input-group-append>
                        </b-input-group>
                        <b-input-group-text class="remove-invest" @click="showInvestInput = !showInvestInput">
                          <b-icon icon="x"/>
                        </b-input-group-text>

                        <b-modal ref="next-step" size="sm" hide-footer :title="$t('views.loan-details.addedWhatNext')">
                          <b-button class="mt-3 confirm-btn" block @click="$router.push({ path: '/confirm-investment'})">{{ $t('views.loan-details.confirmInvestment') }}</b-button>
                          <b-button class="mt-3 invest" block @click="$router.push({ path:'/invest?tab=2'})">{{ $t('views.loan-details.listOfInvestments') }}</b-button>
                          <b-button class="mt-2 continue" block @click="$router.push({ path: '/investments'})">{{ $t('views.loan-details.myInvestments') }}</b-button>
                        </b-modal>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <h3>{{ $t('views.loan-details.loanDetails') }}</h3>
            <div class="row">
              <div class="col col-lg-12 col-12 col-sm-12">
                <div class="income-box">
                  <table class="loan-details">
                    <tr>
                      <td class="label">{{ $t('views.loan-details.loanType') }}</td>
                      <td class="value">{{ loanDetails.loan_type_classificators_name_i18n ? $t(loanDetails.loan_type_classificators_name_i18n) : loanDetails.loan_type_classificators_name }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.loanAmount') }}</td>
                      <td class="value">{{ loanDetails.loan_issued_amount | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                        <span v-if="loanDetails.loan_currency !== 'EUR'">
                            ({{ loanDetails.loan_issued_amount_in_eur | moneyFormatterWithCurrency('EUR') }})
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.remainingPrincipal') }}</td>
                      <td class="value">{{ loanDetails.loan_remaining_principal | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                        <span v-if="loanDetails.loan_currency !== 'EUR'">
                            ({{ loanDetails.loan_remaining_principal_in_eur | moneyFormatterWithCurrency('EUR') }})
                        </span>
                      </td>
                    </tr>
                    <tr v-if="loanDetails.loan_effective_apr">
                      <td class="label">{{ $t('views.loan-details.aprForABorrower') }}
                        <help-tooltip :tooltipText="$t('views.loan-details.aprForABorrowerTooltip')"></help-tooltip>
                      </td>
                      <td class="value">{{ showAsPercentages(loanDetails.loan_effective_apr) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.interestRateOffered') }}</td>
                      <td class="value">{{ showAsPercentages(loanDetails.loan_investor_interest_rate) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.buybackGuarantee') }}</td>
                      <td class="value">
                        <div v-if="loanDetails.buyback_guarantee_days">{{ $t('views.loan-details.yesD', {'1': loanDetails.buyback_guarantee_days}) }}</div>
                        <div v-else-if="loanDetails.loan_buyback_guarantee">{{ $t('views.loan-details.yes') }}</div>
                        <div v-else-if="!loanDetails.loan_buyback_guarantee">{{ $t('investorprofile.no') }}</div>
                      </td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.buybackDate') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.buyback_execution_date || loanDetails.loan_buyback_date) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.initialTerm') }}</td>
                      <td class="value">{{ showDiffInTermFormat(loanDetails.loan_initial_due_date, loanDetails.loan_issued_date) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.remainingTerm') }}</td>
                      <td class="value">{{ showDiffInTermFormat(loanDetails.loan_due_date, new Date().toDateString()) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.listingDate') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.loan_list_datetime) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.dateOfIssue') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.loan_issued_date) }}</td>
                    </tr>
                    <tr v-if="extendableSchedule && extensionUsed">
                      <td class="label">{{ $t('views.loan-details.initialClosingDate') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.loan_initial_due_date) }}</td>
                    </tr>
                    <tr v-if="extendableSchedule && extensionUsed">
                      <td class="label">{{ $t('views.loan-details.extendedClosingDate') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.loan_due_date) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.scheduleExtensions') }}</td>
                      <td class="value" v-if="extendableSchedule">
                        {{ loanDetails.loan_extension_used_count ? loanDetails.loan_extension_used_count : 0 }}
                        <span v-if="loanDetails.loan_extension_used_count && (loanDetails.loan_extension_used_count<loanDetails.loan_extension_count)">
                          {{ $t('views.loan-details.of', {'1': loanDetails.loan_extension_count ? loanDetails.loan_extension_count : 0}) }}
                        </span>
                      </td>
                      <td class="value" v-if="!extendableSchedule">{{ $t('views.loan-details.notAllowed') }}</td>
                    </tr>
                    <tr v-if="!extendableSchedule || (extendableSchedule && !extensionUsed)">
                      <td class="label">{{ $t('views.loan-details.closingDate') }}</td>
                      <td class="value">{{ showDateInDotFormat(loanDetails.loan_due_date) }}</td>
                    </tr>
                    <tr>
                      <td class="label">{{ $t('views.loan-details.status') }}</td>
                      <td class="value">{{ loanDetails.loan_status_i18n ? $t(loanDetails.loan_status_i18n) : loanDetails.loan_status }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
            <div class="row bottom-stepper">
            <div class="col-12">
              <b-tabs navWrapperClass="income-tabs" pills="" class="w-100">
                <b-tab v-for="i in loanSteps" :key="'tab-' + i.id" :title="$t(i.title)" :active="i.id===loanStep" @click="setLoanStep(i.id)"></b-tab>
              </b-tabs>
            </div>
          </div>
            <div class="loan-borrower-details income-box">
              <div v-show="(this.loanStep===1)">
                <table class="loan-details">
                  <tr>
                    <td class="label">{{ $t('views.loan-details.borrower') }}</td>
                    <td class="value">{{ loanDetails.loan_borrower_name }}</td>
                  </tr>
                  <tr v-if="loanDetails.loan_collateral_link">
                    <td class="label">{{ $t('views.loan-details.collateral') }}</td>
                    <td class="value"><a v-bind:href=loanDetails.loan_collateral_link>{{ $t('views.loan-details.clickForDetails') }}</a></td>
                  </tr>
                  <tr v-show="(loanDetails.loan_borrower_interest>0)">
                    <td class="label">{{ $t('views.loan-details.borrowerYearlyInterestRate') }}</td>
                    <td class="value">{{ showAsPercentages(loanDetails.loan_borrower_interest) }}</td>
                  </tr>
                  <tr v-if="loanDetails.loan_purpose">
                    <td class="label">{{ $t('views.loan-details.loanPurpose') }}</td>
                    <td class="value">{{ loanDetails.loan_purpose }}</td>
                  </tr>
                  <tr>
                    <td class="label">{{ $t('views.loan-details.issuanceCountry') }}</td>
                    <td class="value">{{
                        loanDetails.loan_country_classificators_name_i18n ? $t(loanDetails.loan_country_classificators_name_i18n) : loanDetails.loan_country_classificators_name
                      }}
                    </td>
                  </tr>
                </table>
              </div>
              <div v-show="(this.loanStep===2)">
                <div class="w-100" v-if="extendableSchedule && extensionUsed">
                  <p class="text-right"><a href="#" @click="showScheduleDetails = true">{{ $t('views.loan-details.previousSchedules') }}</a>
                    <help-tooltip :tooltip-text="$t('views.loan-details.historicalPaymentSchedules')"></help-tooltip>
                  </p>
                  <!-- target="_blank" :href="'/payment-schedule-details/' + loanDetails.loan_ref"  -->
                </div>
                <b-table class="payment-schedule" responsive hover :items="getScheduleData()">
                  <template v-slot:head()="data">
                    <span class="table-header">{{ dynamicHeaderTranslate(data.field.key) }}</span>
                  </template>
                  <template v-slot:cell(date)="{ value }">
                    {{ showDateInDotFormat(value) }}
                  </template>
                  <template v-slot:cell(principal)="{ value }">
                    {{ value | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                  </template>
                  <template v-slot:cell(interest)="{ value }">
                    {{ value | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                  </template>
                  <template v-slot:cell(monthly_fee)="{ value }">
                    {{ value | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                  </template>
                  <template v-slot:cell(total)="{ value }">
                    {{ value | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                  </template>
                  <template v-slot:cell(status)="{ value }">
                    {{ value }}
                  </template>
                  <template v-slot:cell()="data">
                    {{ data.value | moneyFormatterWithCurrency(loanDetails.loan_currency) }}
                  </template>
                  <template v-slot:cell(payment_received)="{ value }">
                    <div class="text-center">{{ value ? showDateInDotFormat(value) : '-' }}</div>
                  </template>
                  <template v-slot:cell(details)="{ value }">
                    <div class="text-center"><a :href="value">{{ value }}</a></div>
                  </template>
                </b-table>
              </div>
              <div v-show="(this.loanStep===3)">
                <b-table class="payment-schedule" hover :items="getInvestmentsData()">
                  <template v-slot:head()="data">
                    <span class="table-header">{{ $t('views.loan-details.investments.title.' + data.field.key) }}</span>
                  </template>
                  <template v-slot:cell(invested)="{ value }">
                    {{ value | moneyFormatter }}
                  </template>
                  <template v-slot:cell(repaid)="{ value }">
                    {{ value | moneyFormatter }}
                  </template>
                  <template v-slot:cell(interest_received)="{ value }">
                    {{ value | moneyFormatter }}
                  </template>
                  <template v-slot:head(pdf_link)><span><!--empty header--></span></template>
                  <template v-slot:head(pdf_not_ready)><span><!--empty header--></span></template>
                  <template v-slot:cell(pdf_link)="{ item }">
                    <a v-if="item.pdf_link" target="_blank" :href="baseApiUrl + item.pdf_link"><img src="../assets/pdf.svg" alt="Assignment agreement"/></a>
                    <img v-if="item.pdf_not_ready" src="../assets/generating.svg" width="18px" v-b-tooltip.hover :title="$t('views.loan-details.assignmentAgreementIs')"
                         alt="Assignment agreement is being prepared, try again in couple minutes!"/>
                  </template>
                  <template v-slot:cell(pdf_not_ready)><span><!--empty pdf_not_ready--></span></template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-if="notFound">
          <div class="col col-12 col-sm-12">
            <button type="button" @click="$router.go(-1)" class="btn">&laquo;{{ $t('views.loan-details.laquoBack') }}</button>
            <h2>Unfortunately, a loan was not found.</h2>
            <h3>Please, check the loan id in the browser URL.</h3>
          </div>
        </div>
      </div>
    </div>

    <b-modal
        centered
        scrollable
        v-model="showScheduleDetails"
        size="lg"
    >
      <template #modal-header="{ close }">
        <div class="w-100 modal-header-top">{{ $t('views.loan-details.previousSchedules1') }}</div>
        <b-button size="sm" variant="outline" @click="close()">X</b-button>
      </template>
      <div class="w-100">
        <payment-schedule-details :openNew="true"></payment-schedule-details>
      </div>
      <template v-slot:modal-footer>
        <div class="w-100"></div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import tmpLoanDetails from '../config/tmp_loan_details.json'
import moneyMixin from '../mixins/money'
import imageMixin from '../mixins/image'
import dateMixin from '../mixins/date'
import DoughnutChart from '@/components/charts/complex/DoughnutChartDetailsSection'
import Loader from '../components/Loader'
import loans from '../api/loans'
import investConfig from '../config/invests.json'
import classificatorDataHelper from '@/helpers/classificatorDataHelper'
import HelpTooltip from '@/components/HelpTooltip'
import trackingHelper from '@/helpers/trackingHelper'
import PaymentScheduleDetails from '@/components/Payment-schedule-details'

export default {
  name: 'Loan-details',
  components: {PaymentScheduleDetails, Loader, DoughnutChart, HelpTooltip},
  mixins: [moneyMixin, imageMixin, dateMixin],
  data()
  {
    return {
      isLoading: true,
      investConfig: investConfig,
      loanDetails: tmpLoanDetails,
      investInput: '',
      showInvestInput: false,
      loanSteps: [
        {id: 1, title: 'views.loan-details.tabs.borrowerInfo', active: true, completed: false},
        {id: 2, title: 'views.loan-details.tabs.paymentSchedule', active: false, completed: false},
        {id: 3, title: 'views.loan-details.tabs.investmentBreakdown', active: false, completed: false}
      ],
      loanStatus: 'Current',
      loanStep: 1,
      chartData: {},
      baseApiUrl: process.env.VUE_APP_API_BASE_URL,
      minAmount: investConfig.minLimitAmount,
      extendableSchedule: false,
      extensionUsed: false,
      showScheduleDetails: false,
      scheduleDetails: null,
      notFound: false
    }
  },
  mounted()
  {
    const loanId = this.$route.params.id
    loans.getDetails(loanId)
        .then(response =>
        {
          if (response.loan_ref)
          {
            this.loanDetails = Object.assign({}, tmpLoanDetails, response)
            this.extendableSchedule = this.loanDetails.loan_extendable_schedule === 1
            this.extensionUsed = (this.loanDetails.loan_extension_used_count && this.loanDetails.loan_extension_used_count > 0)

            const statusesData = [
              {
                name: this.loanDetails.originator_details.name + ' ' + this.$t('views.loan-details.juniorShare'),
                percent: this.loanDetails.breakdown_chart.skin_the_game_percent,
                amount: this.loanDetails.breakdown_chart.skin_the_game_amount,
                amount_in_eur: this.loanDetails.breakdown_chart.skin_the_game_amount_in_eur,
                currency: this.loanDetails.loan_currency,
                help: 'views.loan-details.thisIsThe',
                color: '#DBDDDE'
              },
              {
                name: 'Your share',
                percent: this.loanDetails.breakdown_chart.your_share_percent,
                amount: this.loanDetails.breakdown_chart.your_share_amount,
                currency: 'EUR',
                color: '#BB86FC'
              },
              {
                name: 'Investors share',
                percent: this.loanDetails.breakdown_chart.investor_share_percent,
                amount: this.loanDetails.breakdown_chart.investor_share_amount_in_eur,
                currency: 'EUR',
                color: '#4CAF50'
              },
              {
                name: this.loanDetails.originator_details.name + ' share',
                percent: this.loanDetails.breakdown_chart.lo_share_percent,
                amount: this.loanDetails.breakdown_chart.lo_share_amount,
                amount_in_eur: this.loanDetails.breakdown_chart.lo_share_amount_in_eur,
                currency: this.loanDetails.loan_currency,
                help: 'views.loan-details.thisAmountIs',
                color: '#30A7FE'
              }
            ]
            this.chartData = [{
              categoryName: 'Loans details',
              companyName: this.loanDetails.originator_details.name,
              companyUrl: this.loanDetails.originator_details.name,
              companyLogo: this.loanDetails.originator_details.logo,
              companyEntity: this.loanDetails.originator_details.legal_entity,
              data: statusesData
            }]

            response.loan_type = response.loan_type_classificators_name
            response.loan_originator_name = response.originator_details.name ? response.originator_details.name : ''
            trackingHelper.pushToDataLayer('prodDetail', [response], {list: 'Loan details'})
          }
        })
        .catch(error =>
        {
          if (error.response.status === 404)
          {
            this.notFound = true
            this.isLoading = false
          }
          console.log(error)
        })
        .finally(() => (this.isLoading = false))
  },
  watch: {
    '$store.state.investors.investor': function (investor)
    {
      if (investor && investor.investor_available_funds)
      {
        if (parseInt(investor.investor_available_funds) < investConfig.minLimitAmount)
        {
          this.minAmount = parseFloat(investor.investor_available_funds.toFixed(2))
        }
      }
    },
    '$route.query.step': {
      handler: function (step)
      {
        if (step)
        {
          this.setLoanStep(parseInt(step))
          for (const index in this.loanSteps)
          {
            this.loanSteps[index].active = this.loanSteps[index].id === this.loanStep
          }
        }
      },
      deep: true,
      immediate: true
    },
    loanStep: function (number)
    {
      try
      {
        // Set completed previous steps
        this.loanSteps.filter(s => s.id < number).forEach(function (step)
        {
          step.active = false
        })
        // Set not active/completed next elements
        this.loanSteps.filter(s => s.id > number).forEach(function (step)
        {
          step.active = false
          step.completed = false
        })
        // Set active current selected step
        this.loanSteps.filter(s => s.id === number).shift().active = true
      }
      catch (e)
      {
        console.log('Something went wrong with changing loanSteps', e)
      }
    }
  },
  methods: {
    setLoanStep(number)
    {
      this.loanStep = number
    },
    getInvestmentsData()
    {
      const resultData = []
      if (this.loanDetails.investment_list && this.loanDetails.investment_list.length)
      {
        this.loanDetails.investment_list.forEach(
            function (investment)
            {
              const investItem = {}
              investItem.investor_name = (investment.investor_first_name ? investment.investor_first_name : '') + ' ' + (investment.investor_last_name ? investment.investor_last_name : '')
              investItem.invested = investment.investment_invest_amount ? parseFloat(investment.investment_invest_amount) : ''
              investItem.repaid = investment.investment_received_amount ? parseFloat(investment.investment_received_amount) : ''
              investItem.interest_received = investment.investment_interest_amount ? parseFloat(investment.investment_interest_amount) : ''
              investItem.pdf_link = investment.pdf_link ? investment.pdf_link : ''
              investItem.pdf_not_ready = investment.pdf_not_ready ? investment.pdf_not_ready : ''
              resultData.push(investItem)
            }
        )
      }
      return resultData
    },
    getScheduleData()
    {
      const resultData = []
      const propsSum = []
      if (this.loanDetails.loan_schedule && this.loanDetails.loan_schedule.schedule && this.loanDetails.loan_schedule.schedule.length)
      {
        this.loanDetails.loan_schedule.schedule.forEach(
            function (schedule)
            {
              if (schedule.schedule_components)
              {
                const withoutZeroPrices = []
                let sum = 0
                Object.keys(schedule.schedule_components).forEach(function (key)
                {
                  const value = parseFloat(schedule.schedule_components[key])
                  if (key === 'capital')
                  {
                    key = 'principal'
                  }
                  sum += value
                  propsSum[key] = (propsSum[key] ? propsSum[key] : 0) + value
                  withoutZeroPrices[key] = value
                })
                let scheduleItem = {}
                scheduleItem.date = schedule.date
                scheduleItem = {...scheduleItem, ...withoutZeroPrices}
                scheduleItem.total = parseFloat(sum.toFixed(2))
                scheduleItem.payment_received = null
                scheduleItem.status = this.$t('views.loan-details.scheduleItem.unpaid')
                if (schedule.repayment && schedule.repayment.total > 0)
                {
                  scheduleItem.status = (schedule.repayment.repaid ? this.$t('views.loan-details.scheduleItem.paid') : this.$t('views.loan-details.scheduleItem.unpaid'))
                  if (schedule.repayment.payments && schedule.repayment.payments.length)
                  {
                    scheduleItem.payment_received = Math.max.apply(Math, schedule.repayment.payments.map((o) =>
                    {
                      return (new Date(o.date)).getTime()
                    }))
                  }
                }
                resultData.push(scheduleItem)
              }
            }
            , this)
      }

      // Remove 0 schedule elements section
      try
      {
        for (const k in propsSum)
        {
          // eslint-disable-next-line no-prototype-builtins
          if (propsSum.hasOwnProperty(k))
          {
            if (propsSum[k] === 0)
            {
              // console.log('NEED to remove ', k, 'from: ', resultData)
              for (const key in resultData)
              {
                // eslint-disable-next-line no-prototype-builtins
                if (resultData[key].hasOwnProperty(k))
                {
                  delete resultData[key][k]
                }
              }
            }
          }
        }
      }
      catch (e)
      {
        console.error('Error removing 0-value schedule elements ', e)
      }

      return resultData
    },
    isValid()
    {
      if (this.investInput)
      {
        const investInput = parseFloat(parseFloat(this.investInput).toFixed(2))
        const minAmount = parseFloat(this.minAmount).toFixed(2)
        const investableAmount = parseFloat(parseFloat(this.loanDetails.investable_amount).toFixed(2))
        // console.log('1: ', investInput < minAmount)
        // console.log('2: ', investInput, investableAmount, ' => ', investInput > investableAmount)
        return !(investInput < minAmount || investInput > investableAmount || (investableAmount <= 1 && investInput !== investableAmount))
      }
      return false
    },
    addInvest()
    {
      const amount = this.investInput
      if (this.loanDetails.loan_ref && amount)
      {
        if (this.isValid())
        {
          this.loanDetails.amount = amount
          this.loanDetails.country = classificatorDataHelper.getShortNameById('COUNTRY', this.loanDetails.loan_country_id)
          this.loanDetails.loan_type = this.loanDetails.loan_type_classificators_name
          this.loanDetails.originator_name = this.loanDetails.originator_details.name ? this.loanDetails.originator_details.name : ''
          this.loanDetails.originator_rating = this.loanDetails.originator_details.rating ? this.loanDetails.originator_details.rating : ''
          this.loanDetails.available_amount = this.loanDetails.loan_list_amount ? this.loanDetails.loan_list_amount : ''
          this.loanDetails.loan_originator_name = this.loanDetails.originator_details.name ? this.loanDetails.originator_details.name : ''
          this.$store.dispatch('invests/addInvestItem', this.loanDetails)
          this.showModal()
        }
      }
    },
    showModal()
    {
      this.$refs['next-step'].show()
    },
    showInvestForm()
    {
      this.showInvestInput = true
      this.$nextTick(() =>
      {
        this.$refs[this.loanDetails.loan_ref].focus()
      })
    },
    getMinAmount(maxInvestableAmount)
    {
      const investableAmount = parseFloat(maxInvestableAmount)
      const minAmount = parseFloat(this.minAmount)
      return (investableAmount > 1 ? minAmount : investableAmount).toFixed(2)
    },
    maxRound(currentValue, maxValue, refId)
    {
      const diff = maxValue - currentValue
      if (diff < 1)
      {
        // console.log('DIFF:', diff, ' SET to ', maxValue, ' refId ', refId, ' Prev value', this.$refs[refId].$el.value)
        this.$refs[refId].$el.value = maxValue
      }
    },
    dynamicHeaderTranslate(headerName)
    {
      const lc = 'views.loan-details.schedule.title.' + headerName
      return lc === this.$t(lc) ? headerName : this.$t(lc)
    }
  }
}
</script>

<style lang="scss" scoped>
$default-font-size: 15px;

h2 {
  font-style: normal;
  font-weight: bold;
  font-size: 36px;
  line-height: 40px;
  color: #000000;
  flex: none;
  order: 0;
  align-self: flex-start;
  margin: 50px 0 25px;
}

h3 {
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 30px;
  color: #000000;
  flex: none;
  order: 0;
  align-self: flex-start;
  margin: 0 10px 20px;
}

.loan-details {
  width: 100%;
  //max-width: 676px;

  tr td {
    border-bottom: 1px solid #EDEFF2;
    padding: 4px;
  }

  .value {
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 20px;
    color: #071D2D;
    text-align: right;
  }

  .label {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 150%;
    color: #6D818F;
    flex: none;
    order: 0;
    align-self: center;
    margin: 16px 0;
    max-width: 245px;
  }
}

.payment-schedule {
  font-size: $default-font-size;
}

.small-stepper {
  font-size: $default-font-size;
  font-weight: bold;
  line-height: 20px;
  margin: 20px auto 0 3px;
}

.right-block {
  .header {
    font-size: 24px;
    line-height: 33px;
    text-align: center;
    font-weight: bold;
    letter-spacing: 1px;
    color: #0C2D45;
    margin-top: 25px;
    margin-bottom: 60px;
  }

  .legend {
    .detail {
      font-style: normal;
      font-weight: normal;
      font-size: $default-font-size;
      line-height: 16px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      color: rgba(0, 0, 0, 0.87);

      .circle {
        width: 11px;
        height: 11px;
        border-radius: 4px;
        margin-right: 4px;
        border: 10px solid;
      }
    }
  }

  .note {
    font-style: normal;
    font-weight: normal;
    font-size: 11px;
    line-height: 12px;
    color: #6E777A;
    flex: none;
    order: 2;
    align-self: center;
    margin-top: 16px;
  }

  .invest-available {
    font-style: normal;
    font-weight: normal;
    font-size: $default-font-size;
    line-height: 20px;
    color: #000000;
    text-align: center;
    .afi
    {
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 150%;
      color: #0C2D45;
      margin-bottom: 8px;
    }
    .amount {
      font-style: normal;
      font-weight: bold;
      font-size: 40px;
      line-height: 54px;
      text-align: center;
      color: #0C2D45;
      margin-bottom: 54px;
    }
  }

  .remove-invest {
    background-color: #E2F3FF;
    border: none;
    float: right;
    cursor: pointer;
    padding: 1px;
    margin-top: -10px;
  }

  .invest-form {
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;

    .invalid {
      color: red;
      font-weight: bold;
    }
  }
}

.confirm-btn {
  background-color: #30a7fe !important;
  border-color: #30a7f1 !important;
}

input[type=number]::-webkit-inner-spin-button {
  background-color: #E2F3FF !important;
  color: #E2F3FF !important;
  border-color: #E2F3FF #E2F3FF #E2F3FF #E2F3FF !important;
  opacity: 1;
}

.invest-section{
  text-align: center;
  margin-bottom: 28px;

  .invest-btn {
    background: #30A7FE;
    border-radius: 24px;
    padding-top: 12px;
    margin-top: 20px;
    padding-bottom: 10px;
    border-color: transparent;
    font-weight: bold;
    color: white;
    min-width: 200px;
  }

  .invest-ig {
    border: 1px solid #E2F3FF;
    border-radius: 16px;
    padding: 2px;
    font-style: normal;
    font-weight: normal;
    line-height: 20px;
    margin: 15px 2px 2px;
    /*background: #E2F3FF;*/
    font-size: 24px;
    max-width: 200px;
    height: 52px;
    box-shadow: 1px 3px 16px 0 var(--blue-income-50);

    .prepend {
      margin-left: 10px;
      padding-top: 14px;
      color: var(--blue-income);
    }

    .invest-input, .invest-input:focus, .invest-input:hover {
      border: none;
      border-radius: 0;
      background: transparent;
      outline: 0;
      box-shadow: none;
      font-size: 24px;
      font-weight: bold;
      text-align: center;
    }

    .append {
      float: right;
      cursor: pointer;
      margin-right: 15px;
      margin-top: 14px;

      img {
        height: 20px;
      }
    }
  }
}

.bottom-stepper{
  margin-bottom: 10px;
  margin-top: 25px;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
